import { decodeHTML } from 'entities';
/**
 * Lightweight memoize replacing lodash/memoize to reduce bundle size.
 * Caches results in a Map keyed by the string returned from `resolver`.
 * The returned function exposes its `.cache` Map so callers can inspect
 * or clear it (e.g. to cap memory usage).
 */ export function memoize(fn, resolver) {
    const cache = new Map();
    const memoized = (...args)=>{
        const key = resolver(...args);
        if (cache.has(key)) {
            return cache.get(key);
        }
        const result = fn(...args);
        cache.set(key, result);
        return result;
    };
    memoized.cache = cache;
    return memoized;
}
// eslint-disable-next-line no-script-url
const DISALLOWED_PROTOCOLS = [
    'javascript:'
];
const ALLOWED_PROTOCOLS = [
    'http:',
    'https:',
    'mailto:',
    'tel:',
    'vscode:',
    'cursor:',
    'swish:',
    'stripedev:'
];
const pollutionPattern = `\\b(?:constructor|__defineGetter__|__defineSetter__|hasOwnProperty|__lookupGetter__|__lookupSetter__|isPrototypeOf|propertyIsEnumerable|toString|valueOf|__proto__|toLocaleString)\\b`;
const pollutionRegex = new RegExp(pollutionPattern, 'i');
const parseUrl = (url, base)=>{
    try {
        return new URL(url, base);
    } catch (e) {
        return undefined;
    }
};
const parseUrlLike = (u)=>{
    if (u instanceof URL) {
        return u;
    }
    if (typeof u === 'string') {
        var _parseUrl;
        const resolved = (_parseUrl = parseUrl(u)) !== null && _parseUrl !== void 0 ? _parseUrl : // e.g. when u = /path
        parseUrl(u, // In SSR context, location is not defined which causes this to throw an error.
        // We only use the value to run the sanity check on the URL and we don't return the value,
        // so it is safe to use a static URL if location is undefined. Using localhost in this case
        typeof location !== 'undefined' ? location.origin : 'http://localhost');
        return resolved;
    }
    return undefined;
};
const isUrlProtocolAllowed = (url, allowedProtocols = ALLOWED_PROTOCOLS, // eslint-disable-next-line camelcase
UNSAFE_allowNonDangerousProtocols = false)=>{
    var _parseUrlLike;
    const protocol = (_parseUrlLike = parseUrlLike(url)) === null || _parseUrlLike === void 0 ? void 0 : _parseUrlLike.protocol;
    if (!protocol) {
        return true;
    }
    if (DISALLOWED_PROTOCOLS.includes(protocol)) {
        return false;
    }
    return(// eslint-disable-next-line camelcase
    allowedProtocols.includes(protocol) || UNSAFE_allowNonDangerousProtocols);
};
const containsProtoKeywords = (u)=>{
    const parsedUrl = parseUrlLike(u);
    if (!parsedUrl) {
        return false;
    }
    if (pollutionRegex.test(parsedUrl.pathname)) {
        return true;
    }
    if (pollutionRegex.test(parsedUrl.hash)) {
        return true;
    }
    for (const key of parsedUrl.searchParams.keys()){
        if (pollutionRegex.test(key)) {
            return true;
        }
    }
    return false;
};
export const isSafeUrl = memoize((u, options)=>{
    var _isSafeUrl_cache;
    // Prevent memory leaks by clearing the cache periodically
    if (((_isSafeUrl_cache = isSafeUrl.cache) === null || _isSafeUrl_cache === void 0 ? void 0 : _isSafeUrl_cache.size) === 1000) {
        isSafeUrl.cache.clear();
    }
    const decodedUrl = typeof u === 'string' ? decodeHTML(u) : u;
    var _options_allowedProtocols;
    if (!isUrlProtocolAllowed(decodedUrl, [
        ...ALLOWED_PROTOCOLS,
        ...(_options_allowedProtocols = options === null || options === void 0 ? void 0 : options.allowedProtocols) !== null && _options_allowedProtocols !== void 0 ? _options_allowedProtocols : []
    ], options === null || options === void 0 ? void 0 : options.UNSAFE_allowNonDangerousProtocols)) {
        // eslint-disable-next-line no-console
        console.warn(`[safe-links] redirect (${u}) blocked. URL has invalid protocol`);
        return false;
    }
    if (containsProtoKeywords(decodedUrl)) {
        // eslint-disable-next-line no-console
        console.warn(`[safe-links] redirect (${u}) blocked. URL contains proto keywords`);
        return false;
    }
    return true;
}, (url, options)=>JSON.stringify([
        url,
        typeof location !== 'undefined',
        options
    ]));
export const getTargetWindow = (options)=>{
    switch(options === null || options === void 0 ? void 0 : options.target){
        case 'top':
            return window.top || window;
        case 'parent':
            return window.parent || window;
        default:
            // 'self' and undefined
            return window;
    }
};

//# sourceMappingURL=utils.js.map